import React from 'react';
import { Box, Divider, FormControl, MenuItem, Select, styled, useMediaQuery } from '@mui/material';
import type { SelectChangeEvent, Theme } from '@mui/material';
import FlexBetween from 'components/flexbox/FlexBetween';
import FlexBox from 'components/flexbox/FlexBox';

import { useEffect, useState } from 'react';

import MultiLevelMenu from './MultiLevelMenu';
import MobileSidebar from './MobileSidebar';
import { clearLocalStorage, localSet } from 'utils/utils';
import axiosInstance from 'utils/axios';
import { baseUrl } from 'utils/constants';
import { useNavigate } from 'react-router-dom';
import { EnvBanner } from 'utils/pieces';

interface ProgramItem {
    programGuid: string;
    programName: string;
}

interface DashboardConfig {
    li_dashboard?: {
        headerlogo?: string;
        [key: string]: string | boolean | undefined;
    };
    [key: string]: Record<string, string | boolean | undefined> | undefined;
}

const TOP_HEADER_AREA = 70;
const SidebarWrapper = styled(Box)<{ compact?: number }>(({ theme, compact }) => ({
    height: `100vh`,
    position: `fixed`,
    width: compact ? 86 : 280,
    left: 0,
    transition: `all .2s ease`,
    zIndex: theme.zIndex.drawer,
    backgroundColor: theme.palette.background.paper,
    display: `flex`,
    flexDirection: `column`,
    '&:hover': compact && {
        width: 280,
    },
}));

// Takes the height left under the header and scrolls when the menu is taller.
const NavWrapper = styled(Box)(() => ({
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 16,
    flex: 1,
    minHeight: 0,
    overflowY: `auto`,
    overscrollBehavior: `contain`,
}));

const LogoImg = styled(Box)<{ lilogo?: string; component?: React.ElementType; src?: string; alt?: string }>(
    ({ lilogo }) => ({
        height: lilogo ? `40px` : `35px`,
        margin: `${lilogo ? `10` : `12`}px 0 0 0`,
        width: `auto`,
    })
);

// -----------------------------------------------------------------------------
interface DashboardSidebarProps {
    sidebarCompact: boolean;
    showMobileSideBar: boolean;
    setSidebarCompact: () => void;
    setShowMobileSideBar: () => void;
}

const DashboardSidebar: React.FC<DashboardSidebarProps> = props => {
    const navigate = useNavigate();

    const { sidebarCompact, showMobileSideBar, setShowMobileSideBar } = props;

    const [config, setConfig] = useState<DashboardConfig>(
        JSON.parse(localStorage.getItem(`config`) || JSON.stringify({}))
    );
    const [allPrograms, setAllPrograms] = useState<ProgramItem[]>(
        JSON.parse(localStorage.getItem(`allPrograms`) || JSON.stringify([]))
    );
    const [host] = useState<string>(window.location.host.split(`.`)[0]);
    const [selectedProgram, setSelectedProgram] = useState<string | null>(localStorage.getItem(`selectedProgram`));
    const downLg = useMediaQuery((theme: Theme) => theme.breakpoints.down(`lg`)); // Activate compact when toggle button clicked and not on hover state

    const Separator = styled(Box)(({ theme }) => ({
        height: `40px`,
        width: `1px`,
        backgroundColor: theme.palette.divider,
        margin: `10px 45px`,
    }));

    const listenerUpdate = (): void => {
        setAllPrograms(JSON.parse(localStorage.getItem(`allPrograms`) || JSON.stringify([])));
        setSelectedProgram(localStorage.getItem(`selectedProgram`));
        setConfig(JSON.parse(localStorage.getItem(`config`) || JSON.stringify({})));
    };

    const switchProgram = (v: string): Promise<void> =>
        axiosInstance
            .get(`${baseUrl}switchme?guid=${v}`)
            .then((r: { data: Record<string, string> }) => {
                Object.keys(r.data).forEach((i: string) => {
                    localSet(i, r.data[i]);
                });
                window.location.href = `/dashboard/research`;
            })
            .catch(() => {
                clearLocalStorage();
                navigate(`/login`);
            });

    useEffect(() => {
        window.addEventListener(`storage`, listenerUpdate);
        return () => window.removeEventListener(`storage`, listenerUpdate);
    }, []);

    if (downLg) {
        return (
            <MobileSidebar
                sidebarCompact={false}
                showMobileSideBar={!!showMobileSideBar}
                setShowMobileSideBar={setShowMobileSideBar}
            />
        );
    }

    return (
        <SidebarWrapper compact={sidebarCompact ? 1 : 0}>
            <FlexBetween pt={3} pr={2} pl={4} pb={1} height={TOP_HEADER_AREA} flexShrink={0}>
                {/* LOGO */}
                <FlexBox>
                    <LogoImg component="img" lilogo="true" src="/static/logo/lilogo.svg" alt="logo" />
                    <Separator />
                    <LogoImg component="img" alt="logo" src={config?.li_dashboard?.headerlogo || ``} />
                </FlexBox>
                <Box mx={`auto`}></Box>
            </FlexBetween>
            {!!allPrograms.length && (
                <FlexBetween pt={3} pr={2} pl={4} pb={1} height={TOP_HEADER_AREA} flexShrink={0}>
                    <FormControl fullWidth>
                        <Select
                            sx={{
                                fontSize: `14px`,
                                fontWeight: `500`,
                                color: `#5F748D`,
                                '&:hover': { background: `#F7F7F7` },
                                '& div ': { padding: `7px 12px !important` },
                                minHeight: `unset !important`,
                                borderRadius: `20px !important`,
                            }}
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            fullWidth
                            value={selectedProgram as string}
                            onChange={(v: SelectChangeEvent<string>) => switchProgram(v.target.value)}
                        >
                            {localStorage.programs}
                            {allPrograms.map((a, i) => (
                                <MenuItem
                                    key={i}
                                    sx={{ fontSize: `14px`, fontWeight: `500`, color: `#5F748D` }}
                                    value={a.programGuid}
                                >
                                    {a.programName}
                                </MenuItem>
                            ))}
                            <Divider />
                            <MenuItem
                                sx={{ fontSize: `14px`, fontWeight: `500`, color: `#5F748D` }}
                                value="allprograms"
                            >
                                All Programs
                            </MenuItem>
                        </Select>
                    </FormControl>
                </FlexBetween>
            )}
            {host.startsWith(`localhost`) && <EnvBanner>LOCALHOST</EnvBanner>}
            {host.includes(`-`) && <EnvBanner>{host.split(`-`)[1].toUpperCase()}</EnvBanner>}
            {/* NAVIGATION ITEMS */}
            <NavWrapper>
                <MultiLevelMenu sidebarCompact={false} />
            </NavWrapper>
        </SidebarWrapper>
    );
};

export default DashboardSidebar;
